import { Card, Empty, Spin, Table, Tag, Tabs, Typography } from "antd";
import { Calendar, CalendarClock, History as HistoryIcon, Package, Truck } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useGetHistoryServiceTicketsByInventoryId, useGetUpcomingServiceTicketsByInventoryId } from "~/services/service-ticket";
import { capitalize, formatDate } from "~/utils";
import { JobStatus, ServiceType } from "~/utils/constants";

const { Text } = Typography;

interface HistoryServiceTicketsProps {
  inventoryId: string;
}

const HistoryServiceTickets: React.FC<HistoryServiceTicketsProps> = ({
  inventoryId,
}) => {
  const navigate = useNavigate();
  const { data: historyData, isLoading: isLoadingHistory } =
    useGetHistoryServiceTicketsByInventoryId(inventoryId);
  
  const { data: upcomingData, isLoading: isLoadingUpcoming } =
    useGetUpcomingServiceTicketsByInventoryId(inventoryId);

  const handleRowClick = (record: any) => {
    const bookingId = record.jobTicket?.id;
    if (bookingId) {
      navigate(`/bookings/${bookingId}?tab=services&serviceId=${record.id}`);
    }
  };

  const isLoading = isLoadingHistory || isLoadingUpcoming;

  const getServiceTypeIcon = (type: ServiceType) => {
    switch (type) {
      case ServiceType.DELIVERY:
        return <Truck size={14} />;
      case ServiceType.COLLECTION:
        return <Package size={14} />;
      default:
        return <Calendar size={14} />;
    }
  };

  const getStatusColor = (status: JobStatus) => {
    switch (status) {
      case JobStatus.COMPLETED:
        return "green";
      case JobStatus.CANCELLED:
        return "red";
      case JobStatus.DRAFT:
        return "orange";
      case JobStatus.SCHEDULED:
        return "blue";
      default:
        return "default";
    }
  };

  const columns = [
    {
      title: "Type",
      dataIndex: "serviceType",
      key: "serviceType",
      render: (text: ServiceType) => (
        <div className="flex items-center gap-1">
          {getServiceTypeIcon(text)}
          <span>{capitalize(text)}</span>
        </div>
      ),
    },
    {
      title: "Ticket #",
      dataIndex: "serial",
      key: "serial",
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
      render: (client: any) => client?.companyName || "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: JobStatus) => (
        <Tag color={getStatusColor(status)}>{capitalize(status)}</Tag>
      ),
    },
    {
      title: "Date",
      dataIndex: "serviceDatetime",
      key: "serviceDatetime",
      render: (date: string) => formatDate(date),
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (location: any) => {
        if (!location) return "N/A";
        const city = location.city || "";
        const state = location.state || "";
        if (city && state) return `${city}, ${state}`;
        if (city) return city;
        if (state) return state;
        return "N/A";
      },
    },
  ];

  const renderTable = (data: typeof historyData, isLoading: boolean) => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center p-8">
          <Spin />
        </div>
      );
    }

    if (!data || data.items.length === 0) {
      return (
        <Empty
          description="No service tickets found"
          className="py-8"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }

    return (
      <Table
        dataSource={data.items}
        columns={columns}
        rowKey="id"
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: "pointer" },
        })}
        pagination={
          data.items.length > 10
            ? {
                total: data.meta.totalItems,
                current: data.meta.currentPage,
                pageSize: data.meta.itemsPerPage,
              }
            : false
        }
        size="small"
      />
    );
  };

  return (
    <Card bordered={false} className="!p-0">
      <Tabs
        defaultActiveKey="history"
        size="small"
        items={[
          {
            key: "history",
            label: (
              <span className="flex items-center gap-1.5">
                <HistoryIcon size={14} />
                History
                {historyData && historyData.items.length > 0 && (
                  <span className="text-gray-500 text-xs">
                    ({historyData.items.length})
                  </span>
                )}
              </span>
            ),
            children: renderTable(historyData, isLoadingHistory),
          },
          {
            key: "upcoming",
            label: (
              <span className="flex items-center gap-1.5">
                <CalendarClock size={14} />
                Upcoming
                {upcomingData && upcomingData.items.length > 0 && (
                  <span className="text-gray-500 text-xs">
                    ({upcomingData.items.length})
                  </span>
                )}
              </span>
            ),
            children: renderTable(upcomingData, isLoadingUpcoming),
          },
        ]}
      />
    </Card>
  );
};

export default HistoryServiceTickets;
